/**
 * Unified API Client with caching and deduplication
 * Centralized HTTP client for all API requests
 */

import { API_CONFIG } from '../config/apiConfig';

class ApiClient {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
    this.defaultCacheTTL = API_CONFIG.CACHE.DEFAULT_TTL;
    
    // Smart Data-Flow metrics tracking
    this.metrics = {
      totalRequests: 0,
      cacheHits: 0,
      duplicateRequestsAvoided: 0,
      cacheSize: 0
    };
  }

  /**
   * Main request method with enhanced caching and deduplication
   * Implements Smart Data-Flow principles
   */
  async request(config) {
    this.metrics.totalRequests++;
    const key = this.generateKey(config);
    
    // Smart caching for both GET and safe POST requests (data fetching)
    const isCacheableRequest = this.isCacheableRequest(config);
    
    if (isCacheableRequest && this.cache.has(key)) {
      const cached = this.cache.get(key);
      if (!this.isCacheExpired(cached)) {
        this.metrics.cacheHits++;
        if (API_CONFIG.isDevelopment()) {
          console.log(`📋 [API_CLIENT] Cache HIT (${this.getCacheHitRate()}%) for ${config.url}`);
        }
        return cached.data;
      } else {
        // Remove expired cache entry
        this.cache.delete(key);
      }
    }

    // Deduplicate pending requests (critical for performance)
    if (this.pendingRequests.has(key)) {
      this.metrics.duplicateRequestsAvoided++;
      if (API_CONFIG.isDevelopment()) {
        console.log(`⏳ [API_CLIENT] Deduplicating request for ${config.url}`);
      }
      return this.pendingRequests.get(key);
    }

    if (API_CONFIG.isDevelopment()) {
      console.log(`🌐 [API_CLIENT] Network request for ${config.url}`);
    }

    const requestPromise = this.executeRequest(config, key);
    this.pendingRequests.set(key, requestPromise);

    return requestPromise;
  }

  async executeRequest(config, key) {
    try {
      // Use centralized URL building from API_CONFIG
      const fullURL = API_CONFIG.buildURL(config.url);
      
      // Get default headers and merge with request-specific headers
      const headers = {
        ...API_CONFIG.getDefaultHeaders(),
        ...config.headers,
      };

      // Debug logging in development
      if (API_CONFIG.isDevelopment()) {
        API_CONFIG.log('API Request:', {
          originalUrl: config.url,
          fullURL,
          method: config.method || 'GET',
          headers,
          body: config.body,
        });
      }

      const response = await fetch(fullURL, {
        method: config.method || 'GET',
        headers,
        body: config.body ? JSON.stringify(config.body) : undefined,
        timeout: config.timeout || API_CONFIG.TIMEOUT.DEFAULT,
      });

      if (!response.ok) {
        const errorDetails = {
          status: response.status,
          statusText: response.statusText,
          url: fullURL,
          originalUrl: config.url,
          method: config.method || 'GET'
        };

        if (API_CONFIG.isDevelopment()) {
          API_CONFIG.log('API Request Failed:', errorDetails);
        }

        throw new Error(`HTTP ${response.status}: ${response.statusText} - URL: ${fullURL}`);
      }

      // Check if response is actually JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Server returned non-JSON response: ${text.substring(0, 100)}...`);
      }

      const data = await response.json();
      
      // Smart caching for cacheable requests
      if (this.isCacheableRequest(config)) {
        this.cache.set(key, {
          data,
          timestamp: Date.now(),
          ttl: config.cacheTTL || this.defaultCacheTTL,
        });
        this.metrics.cacheSize = this.cache.size;
        
        if (API_CONFIG.isDevelopment()) {
          console.log(`📋 [API_CLIENT] Cached response for ${config.url} (cache size: ${this.cache.size})`);
        }
      }

      return data;
    } catch (error) {
      if (API_CONFIG.isDevelopment()) {
        API_CONFIG.log('API Request failed:', error);
      }
      throw error;
    } finally {
      this.pendingRequests.delete(key);
    }
  }

  generateKey(config) {
    const method = config.method || 'GET';
    const url = config.url;
    const body = config.body ? JSON.stringify(config.body) : '';
    const params = config.params ? JSON.stringify(config.params) : '';
    return `${method}:${url}:${body}:${params}`;
  }

  isCacheExpired(cached) {
    return Date.now() - cached.timestamp > cached.ttl;
  }

  /**
   * Determine if a request is cacheable
   * Smart Data-Flow: Cache safe data-fetching operations
   */
  isCacheableRequest(config) {
    const method = config.method || 'GET';
    const url = config.url || '';
    
    // Cache GET requests
    if (method === 'GET') return true;
    
    // Cache safe POST requests that are clearly data-fetching operations
    const isDataFetching = url.includes('Get') || 
                          url.includes('List') || 
                          url.includes('Details') ||
                          url.includes('Search');
    
    // Don't cache operations that modify data
    const isModifyingOperation = url.includes('Add') || 
                               url.includes('Edit') || 
                               url.includes('Update') || 
                               url.includes('Delete') ||
                               url.includes('Cancel') ||
                               url.includes('Transfer') ||
                               url.includes('Suspend');
    
    return method === 'POST' && isDataFetching && !isModifyingOperation;
  }

  /**
   * Get current cache hit rate percentage
   */
  getCacheHitRate() {
    if (this.metrics.totalRequests === 0) return 0;
    return ((this.metrics.cacheHits / this.metrics.totalRequests) * 100).toFixed(2);
  }

  /**
   * Invalidate cache entries matching pattern
   * Smart Data-Flow: Intelligent cache invalidation
   */
  invalidateCache(pattern) {
    let invalidated = 0;
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
        invalidated++;
      }
    }
    this.metrics.cacheSize = this.cache.size;
    
    if (API_CONFIG.isDevelopment() && invalidated > 0) {
      console.log(`🗑️ [API_CLIENT] Invalidated ${invalidated} cache entries matching "${pattern}"`);
    }
  }

  /**
   * Smart cache invalidation based on entity operations
   */
  invalidateCacheByEntity(entityType) {
    const patterns = [
      entityType.toLowerCase(),
      entityType.charAt(0).toUpperCase() + entityType.slice(1).toLowerCase(),
      entityType.toUpperCase()
    ];
    
    patterns.forEach(pattern => this.invalidateCache(pattern));
  }

  /**
   * Clear all cache
   */
  clearCache() {
    this.cache.clear();
    this.metrics.cacheSize = 0;
    if (API_CONFIG.isDevelopment()) {
      console.log('🧹 [API_CLIENT] Cleared all cache');
    }
  }

  /**
   * Get comprehensive cache and performance statistics
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
      metrics: this.metrics,
      cacheHitRate: this.getCacheHitRate() + '%'
    };
  }

  /**
   * Log performance metrics (Smart Data-Flow monitoring)
   */
  logMetrics() {
    const hitRate = this.getCacheHitRate();
    console.group('📊 [API_CLIENT] Smart Data-Flow Metrics');
    console.log(`🎯 Cache Hit Rate: ${hitRate}%`);
    console.log(`📡 Total Requests: ${this.metrics.totalRequests}`);
    console.log(`📋 Cache Hits: ${this.metrics.cacheHits}`);
    console.log(`⚡ Duplicate Requests Avoided: ${this.metrics.duplicateRequestsAvoided}`);
    console.log(`💾 Cache Size: ${this.cache.size} entries`);
    console.log(`🚀 Performance Gain: ~${Math.round(hitRate * 0.4)}% estimated load time reduction`);
    console.groupEnd();
  }

  /**
   * Reset metrics (for testing or monitoring periods)
   */
  resetMetrics() {
    this.metrics = {
      totalRequests: 0,
      cacheHits: 0,
      duplicateRequestsAvoided: 0,
      cacheSize: this.cache.size
    };
  }

  /**
   * Test proxy configuration in development
   */
  async testProxy() {
    return API_CONFIG.testProxy();
  }

  /**
   * Enhanced convenience methods with Smart Data-Flow cache invalidation
   */
  get(url, config = {}) {
    return this.request({ ...config, url, method: 'GET' });
  }

  async post(url, body, config = {}) {
    const response = await this.request({ ...config, url, method: 'POST', body });
    
    // Smart cache invalidation for modifying operations
    if (this.isModifyingOperation(url)) {
      const entityType = this.extractEntityFromUrl(url);
      if (entityType) {
        this.invalidateCacheByEntity(entityType);
        if (API_CONFIG.isDevelopment()) {
          console.log(`🗑️ [API_CLIENT] Auto-invalidated ${entityType} cache after POST operation`);
        }
      }
    }
    
    return response;
  }

  async put(url, body, config = {}) {
    const response = await this.request({ ...config, url, method: 'PUT', body });
    
    // Auto-invalidate cache for PUT operations
    const entityType = this.extractEntityFromUrl(url);
    if (entityType) {
      this.invalidateCacheByEntity(entityType);
    }
    
    return response;
  }

  async patch(url, body, config = {}) {
    const response = await this.request({ ...config, url, method: 'PATCH', body });
    
    // Auto-invalidate cache for PATCH operations  
    const entityType = this.extractEntityFromUrl(url);
    if (entityType) {
      this.invalidateCacheByEntity(entityType);
    }
    
    return response;
  }

  async delete(url, config = {}) {
    const response = await this.request({ ...config, url, method: 'DELETE' });
    
    // Auto-invalidate cache for DELETE operations
    const entityType = this.extractEntityFromUrl(url);
    if (entityType) {
      this.invalidateCacheByEntity(entityType);
    }
    
    return response;
  }

  /**
   * Check if operation modifies data (Smart Data-Flow principle)
   */
  isModifyingOperation(url) {
    return url.includes('Add') || 
           url.includes('Edit') || 
           url.includes('Update') || 
           url.includes('Delete') ||
           url.includes('Cancel') ||
           url.includes('Transfer') ||
           url.includes('Suspend');
  }

  /**
   * Extract entity type from URL for smart cache invalidation
   */
  extractEntityFromUrl(url) {
    const entities = ['Staff', 'Corporate', 'Driver', 'Route', 'Trip', 'Booking', 'Department'];
    
    for (const entity of entities) {
      if (url.includes(entity)) {
        return entity;
      }
    }
    
    return null;
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Add debugging utility for development
if (API_CONFIG.isDevelopment()) {
  // Make apiClient available globally for debugging
  window.apiClient = apiClient;
  
  // Make API_CONFIG available for debugging
  window.API_CONFIG = API_CONFIG;

  // Smart Data-Flow monitoring - log metrics periodically
  setInterval(() => {
    if (apiClient.metrics.totalRequests > 0) {
      apiClient.logMetrics();
    }
  }, 30000); // Log every 30 seconds if there's activity

  // Global functions for manual debugging
  window.logApiMetrics = () => apiClient.logMetrics();
  window.clearApiCache = () => apiClient.clearCache();
  window.getApiStats = () => apiClient.getCacheStats();

  // Auto-test proxy on load (disabled to reduce console noise)
  // setTimeout(() => {
  //   apiClient.testProxy();
  // }, 2000);
}

export default apiClient;